import { useNavigate, useParams } from "@tanstack/react-router"
import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface Contact {
  id: string
  name?: string
  email?: string
  phone?: string
  company?: string
  position?: string
  quality: "hot" | "warm" | "cold"
  tags: string[]
  metadata: Record<string, any>
  conversations?: {
    id: string
    status: string
    platform: string
    campaign?: {
      id: string
      name: string
    }
    currentStage?: {
      id: string
      name: string
    }
    messages?: {
      id: string
      content: string
      direction: "inbound" | "outbound"
      sentAt: string
    }[]
    lastActivityAt: string
    createdAt: string
  }[]
  createdAt: string
  updatedAt: string
}

const QualityBadge: React.FC<{ quality: string }> = ({ quality }) => {
  const colors = {
    hot: "bg-red-100 text-destructive dark:bg-red-800 dark:text-red-300",
    warm: "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-300",
    cold: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-300",
  }

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[quality as keyof typeof colors]}`}>
      {quality.charAt(0).toUpperCase() + quality.slice(1)}
    </span>
  )
}

const ContactDetail: React.FC = () => {
  const params = useParams({ from: "/crm/contacts/$id" })
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<"overview" | "conversations" | "activity">("overview")
  const [editMode, setEditMode] = useState(false)

  const contactId = params.id

  // Query contact details
  const {
    data: contact,
    isLoading,
    refetch,
  } = trpc.crm.contacts.getById.useQuery({ id: contactId }, { enabled: !!contactId })

  // Update contact mutation
  const updateContactMutation = trpc.crm.contacts.update.useMutation({
    onSuccess: () => {
      setEditMode(false)
      void refetch()
    },
  })

  const handleUpdateContact = (formData: FormData) => {
    const name = formData.get("name") as string
    const email = formData.get("email") as string
    const phone = formData.get("phone") as string
    const company = formData.get("company") as string
    const position = formData.get("position") as string
    const quality = formData.get("quality") as "hot" | "warm" | "cold"

    updateContactMutation.mutate({
      id: contactId,
      data: {
        name: name || undefined,
        email: email || undefined,
        phone: phone || undefined,
        quality,
        metadata: {
          ...(contact?.metadata && typeof contact.metadata === "object" ? contact.metadata : {}),
          company: company || undefined,
          position: position || undefined,
        },
      },
    })
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-foregroundx ">
        <div className="text-gray-500">Loading contact...</div>
      </div>
    )
  }

  if (!contact) {
    return (
      <div className="h-full flex items-center justify-center bg-foregroundx ">
        <div className="text-center">
          <div className="text-gray-500 mb-4">Contact not found</div>
          <button
            onClick={() => navigate({ to: "/crm/contacts" })}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Contacts
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto bg-foregroundx ">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <button
                  onClick={() => navigate({ to: "/crm/contacts" })}
                  className=" hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  ← Back
                </button>
                <h1 className="text-3xl font-bold  dark:text-white">
                  {contact.name || contact.email || contact.phone || "Unknown Contact"}
                </h1>
                <QualityBadge quality={contact.quality} />
              </div>
              <div className=" dark:text-gray-400">
                {(contact.metadata as any)?.company || (contact as any).company}
                {((contact.metadata as any)?.position || (contact as any).position) &&
                  ` • ${(contact.metadata as any)?.position || (contact as any).position}`}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setEditMode(!editMode)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                {editMode ? "Cancel" : "Edit"}
              </button>
              <button
                onClick={() =>
                  navigate({
                    to: "/crm/start-conversation",
                    // search: () => ({ contactId: contact.id })
                  })
                }
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                Start Conversation
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-border dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {[
                { key: "overview", label: "Overview", icon: "👤" },
                { key: "conversations", label: "Conversations", icon: "💬" },
                { key: "activity", label: "Activity", icon: "📋" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${
                    activeTab === tab.key
                      ? "border-blue-500  dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:text-primary hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="bg-background  rounded-lg p-6 border border-border dark:border-gray-700">
              <h3 className="text-lg font-semibold  dark:text-white mb-4">Contact Information</h3>

              {editMode ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    handleUpdateContact(new FormData(e.currentTarget))
                  }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Name</label>
                      <input
                        name="name"
                        type="text"
                        defaultValue={contact.name || ""}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Email</label>
                      <input
                        name="email"
                        type="email"
                        defaultValue={contact.email || ""}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Phone</label>
                      <input
                        name="phone"
                        type="tel"
                        defaultValue={contact.phone || ""}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Quality</label>
                      <select
                        name="quality"
                        defaultValue={contact.quality}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      >
                        <option value="hot">Hot</option>
                        <option value="warm">Warm</option>
                        <option value="cold">Cold</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Company</label>
                      <input
                        name="company"
                        type="text"
                        defaultValue={(contact.metadata as any)?.company || (contact as any).company || ""}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Position</label>
                      <input
                        name="position"
                        type="text"
                        defaultValue={(contact.metadata as any)?.position || (contact as any).position || ""}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditMode(false)}
                      className="px-4 py-2  dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updateContactMutation.isLoading}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {updateContactMutation.isLoading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Email</label>
                    <div className=" dark:text-white">{contact.email || "Not provided"}</div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Phone</label>
                    <div className=" dark:text-white">{contact.phone || "Not provided"}</div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Company</label>
                    <div className=" dark:text-white">
                      {(contact.metadata as any)?.company || (contact as any).company || "Not provided"}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Position</label>
                    <div className=" dark:text-white">
                      {(contact.metadata as any)?.position || (contact as any).position || "Not provided"}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Added</label>
                    <div className=" dark:text-white">{new Date(contact.createdAt).toLocaleDateString()}</div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      Last Updated
                    </label>
                    <div className=" dark:text-white">{new Date(contact.updatedAt).toLocaleDateString()}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="bg-background  rounded-lg p-6 border border-border dark:border-gray-700">
              <h3 className="text-lg font-semibold  dark:text-white mb-4">Tags</h3>
              {contact.tags && Array.isArray(contact.tags) && contact.tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {contact.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-300 text-sm font-medium px-3 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500">No tags assigned</div>
              )}
            </div>

            {/* Custom Metadata */}
            {contact.metadata && Object.keys(contact.metadata).length > 0 && (
              <div className="bg-background  rounded-lg p-6 border border-border dark:border-gray-700">
                <h3 className="text-lg font-semibold  dark:text-white mb-4">Additional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(contact.metadata).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                        {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1")}
                      </label>
                      <div className=" dark:text-white">
                        {typeof value === "object" ? JSON.stringify(value) : String(value)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "conversations" && (
          <div className="bg-background  rounded-lg border border-border dark:border-gray-700">
            <div className="p-6 border-b border-border dark:border-gray-700">
              <h3 className="text-lg font-semibold  dark:text-white">Conversation History</h3>
            </div>

            {contact.conversations && contact.conversations.length > 0 ? (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {contact.conversations.map((conversation) => (
                  <div key={conversation.id} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-medium  dark:text-white">
                          {conversation.campaign?.name || "Unknown Campaign"}
                        </h4>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {conversation.platform} • {conversation.status} •{" "}
                          {conversation.currentStage?.name || "No stage"}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Started {new Date(conversation.createdAt).toLocaleDateString()} • Last activity{" "}
                          {new Date(conversation.lastActivityAt).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate({ to: `/crm/inbox/${conversation.id}` })}
                        className=" hover:text-blue-800 text-sm"
                      >
                        View Details
                      </button>
                    </div>

                    {conversation.messages && conversation.messages.length > 0 && (
                      <div className="space-y-2">
                        {conversation.messages.slice(0, 3).map((message) => (
                          <div
                            key={message.id}
                            className={`p-3 rounded-lg max-w-lg ${
                              message.direction === "outbound"
                                ? "bg-blue-50 dark:bg-blue-900/20 ml-auto"
                                : "bg-foregroundx dark:bg-gray-700"
                            }`}
                          >
                            <div className="text-sm  dark:text-white">
                              {message.content.length > 100
                                ? message.content.substring(0, 100) + "..."
                                : message.content}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {message.direction === "outbound" ? "→ Sent" : "← Received"}{" "}
                              {new Date(message.sentAt).toLocaleString()}
                            </div>
                          </div>
                        ))}
                        {conversation.messages.length > 3 && (
                          <div className="text-sm text-gray-500 text-center">
                            +{conversation.messages.length - 3} more messages
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="text-gray-500 mb-4">No conversations yet</div>
                <button
                  onClick={() =>
                    navigate({
                      to: "/crm/start-conversation",
                      // search: () => ({ contactId: contact.id })
                    })
                  }
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  Start First Conversation
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === "activity" && (
          <div className="bg-background  rounded-lg p-6 border border-border dark:border-gray-700">
            <h3 className="text-lg font-semibold  dark:text-white mb-4">Activity Timeline</h3>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                <div>
                  <div className="font-medium  dark:text-white">Contact created</div>
                  <div className="text-sm text-gray-500">{new Date(contact.createdAt).toLocaleString()}</div>
                </div>
              </div>

              {contact.updatedAt !== contact.createdAt && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                  <div>
                    <div className="font-medium  dark:text-white">Contact updated</div>
                    <div className="text-sm text-gray-500">{new Date(contact.updatedAt).toLocaleString()}</div>
                  </div>
                </div>
              )}

              {contact.conversations &&
                contact.conversations.map((conversation) => (
                  <div key={conversation.id} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                    <div>
                      <div className="font-medium  dark:text-white">
                        Started conversation in {conversation.campaign?.name}
                      </div>
                      <div className="text-sm text-gray-500">{new Date(conversation.createdAt).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ContactDetail
